import React, { useEffect, useState } from "react";
import { Form, FormGroup, Label, Input, Row, Col, FormText } from "reactstrap";
import Switch from "react-bootstrap-switch";
import Select, { components } from "react-select";
const AdditionalSettings = ({ additionalSettings, setAdditionalSettings, formDetails, setFormDetails }) => {
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [previousTemplate, setPreviousTemplate] = useState(formDetails.template);

    // Check if a field should be displayed based on its conditional logic
    const shouldDisplayField = (fieldConfig) => {
        if (!fieldConfig.conditional) return true;
      
        const conditional = fieldConfig.conditional;
      
        // Special case for `template` field logic
        if (conditional.field === "template") {
            if (Array.isArray(conditional.value)) {
                return conditional.value.includes(formDetails[conditional.field]);
            }
            return conditional.value === formDetails[conditional.field];
        }
      
        // Handle multiple AND conditions (all)
        if (conditional.all && Array.isArray(conditional.all)) {
            return conditional.all.every(
                ({ field, value }) => formDetails.widget_data?.[field] === value
            );
        }
      
        // Handle single condition
        return formDetails.widget_data?.[conditional.field] === conditional.value;
    };

    // Handle drag-and-drop functionality
    const handleDragStart = (index) => {
        setDraggingIndex(index);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (index, fieldConfig) => {
        if (draggingIndex === null) return;

        setFormDetails((prevState) => {
            const currentOrder = Array.isArray(prevState.widget_data?.match_order) 
                ? [...prevState.widget_data.match_order] 
                : [];
            
            if (currentOrder.length > 0) {
                const reorderedOptions = [...currentOrder];
                const draggedItem = reorderedOptions.splice(draggingIndex, 1)[0];
                reorderedOptions.splice(index, 0, draggedItem);

                return {
                    ...prevState,
                    widget_data: {
                        ...prevState.widget_data,
                        match_order: reorderedOptions,
                    },
                };
            }
            return prevState;
        });

        setDraggingIndex(null);
    };

    // Initialize match_order from backend defaults if it doesn't exist
    const ensureMatchOrderExists = () => {
        // Make sure widget_data exists
        if (!formDetails.widget_data) {
            setFormDetails(prev => ({
                ...prev,
                widget_data: {}
            }));
            return;
        }

        // Check if match_order exists and is valid
        const templateType = formDetails.template;
        const matchOrderField = additionalSettings.items.match_order;
        
        if (!Array.isArray(formDetails.widget_data.match_order) && 
            matchOrderField?.default && 
            Array.isArray(matchOrderField.default[templateType])) {
            
            // Initialize with default from backend
            setFormDetails(prev => ({
                ...prev,
                widget_data: {
                    ...prev.widget_data,
                    match_order: [...matchOrderField.default[templateType]]
                }
            }));
        }
    };

    // Render function for form fields
    const renderField = (key, fieldConfig) => {
        if (!shouldDisplayField(fieldConfig)) return null;

        switch (fieldConfig.type) {
            case "text":
                return (
                    <>
                    <Input
                        type={fieldConfig.type}
                        id={key}
                        placeholder={fieldConfig.label}
                        value={formDetails.widget_data?.[key] || ""}
                        autoComplete="off"
                        onChange={(e) =>
                            setFormDetails((prev) => ({
                                ...prev,
                                widget_data: { ...prev.widget_data, [key]: e.target.value },
                            }))
                        }
                    />
                        {fieldConfig.helperText && (
                            <FormText>
                                <code>{fieldConfig.helperText}</code>
                            </FormText>
                        )}
                    </>
                );
                case "single_select":
                    return (
                        <Row key={key} className="mb-3">
                            <div className="mb-6">
                               
                            </div>
                            <Select

                            styles={{
                                container: (base) => ({
                                    ...base,
                                    width: '200px',
                                }),
                            }}
                          
                            value={
                                fieldConfig.options?.find(
                                    (option) => option.value === formDetails.widget_data?.[key]
                                ) || null
                            }
                            options={fieldConfig.options || []}
                            onChange={(selectedOption) =>
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: { ...prev.widget_data, [key]: selectedOption?.value },
                                }))
                            }
                        />
                        </Row>
                    );
            case "hex_color":
                const colorValue = formDetails.widget_data?.[key] ?? fieldConfig.default ?? "#ffffff";
            
                return (
                    <div className="inptFomColr" style={{ display: "flex", alignItems: "center" }}>
                        <div className="clrChng" style={{ border: "none", padding: "0" }}>
                            <Input
                                type="color"
                                value={colorValue}
                                onChange={(e) =>
                                    setFormDetails((prev) => ({
                                        ...prev,
                                        widget_data: {
                                            ...prev.widget_data,
                                            [key]: e.target.value,
                                        },
                                    }))
                                }
                                style={{ padding: "0" }}
                                className="h-8 mr-2 rounded cursor-pointer"
                            />
                        </div>
            
                        <div className="clrTxt">
                            <input
                                type="text"
                                value={colorValue}
                                onChange={(e) =>
                                    setFormDetails((prev) => ({
                                        ...prev,
                                        widget_data: {
                                            ...prev.widget_data,
                                            [key]: e.target.value,
                                        },
                                    }))
                                }
                                className="p-2 border rounded"
                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                placeholder="e.g. #FFFFFF"
                            />
                        </div>
                    </div>
                );

            case "number":
                return (
                    <Input
                        type="number"
                        id={key}
                        placeholder={fieldConfig.label}
                        value={formDetails.widget_data?.[key] || ""}
                        min="0" 
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || Number(value) >= 0) {
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: { ...prev.widget_data, [key]: value },
                                }));
                            }
                        }}
                    />
                );

            case "textArea":
                return (
                    <Input
                        type="textarea"
                        id={key}
                        placeholder={fieldConfig.label}
                        value={formDetails.widget_data?.[key] || ""}
                        onChange={(e) =>
                            setFormDetails((prev) => ({
                                ...prev,
                                widget_data: { ...prev.widget_data, [key]: e.target.value },
                            }))
                        }
                    />
                );
            case "switch":
                return (
                    <Switch
                        id={key}
                        offColor="danger"
                        onColor="success"
                        offText={<i className="nc-icon nc-simple-remove" />}
                        onText={<i className="nc-icon nc-check-2" />}
                        value={formDetails.widget_data?.[key] || false}
                        onChange={() =>
                            setFormDetails((prev) => ({
                                ...prev,
                                widget_data: {
                                    ...prev.widget_data,
                                    [key]: !prev.widget_data?.[key],
                                },
                            }))
                        }
                    />
                );
            case "radio":
                if (!fieldConfig.options || !Array.isArray(fieldConfig.options)) {
                    return <div>No options available</div>;
                }
                
                return fieldConfig.options.map((option) => (
                    <Label key={option} check className="d-flex align-items-center gap-1">
                        <Input
                            type="radio"
                            name={key}
                            value={option}
                            checked={formDetails.widget_data?.[key] === option || (!formDetails.widget_data?.[key] && fieldConfig.default === option)}
                            onChange={(e) =>
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: { ...prev.widget_data, [key]: e.target.value },
                                }))
                            }
                        />
                        {option}
                    </Label>
                ));
            case "drag-drop":
                // Ensure match_order exists and is an array
                const matchOrderArray = Array.isArray(formDetails.widget_data?.match_order) 
                    ? formDetails.widget_data.match_order 
                    : [];
                
                return (
                    <FormGroup>
                        <div
                            id="order_input"
                            className="border p-2 rounded bg-white d-flex align-items-center"
                            style={{
                                minHeight: "40px",
                                cursor: "grab",
                                whiteSpace: "nowrap",
                                overflow: "auto",
                            }}
                        >
                            {matchOrderArray.map((option, index) => (
                                <React.Fragment key={option}>
                                    <span
                                        draggable
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={handleDragOver}
                                        onDrop={() => handleDrop(index, fieldConfig)}
                                        className="px-2 py-1 rounded bg-light border"
                                        style={{ cursor: "grab", display: "inline-block" }}
                                    >
                                        {option}
                                    </span>
                                    {index !== matchOrderArray.length - 1 && (
                                        <span className="mx-2 text-muted">{">"}</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <Input 
                            type="hidden" 
                            value={matchOrderArray.join(" > ")} 
                            readOnly 
                        />
                    </FormGroup>
                );
            default:
                return null;
        }
    };

    // Initialize default values on first render
    useEffect(() => {
        if (!formDetails.widget_data) {
            setFormDetails(prev => ({
                ...prev,
                widget_data: {}
            }));
            return;
        }

        const updatedWidgetData = { ...formDetails.widget_data };
    
        Object.entries(additionalSettings.items).forEach(([key, fieldConfig]) => {
            // Only populate if it doesn't already exist in widget_data
            if (updatedWidgetData[key] === undefined && fieldConfig.default !== undefined) {
                // Special handling for match_order which has template-specific defaults
                if (key === 'match_order') {
                    const templateType = formDetails.template;
                    if (fieldConfig.default && Array.isArray(fieldConfig.default[templateType])) {
                        updatedWidgetData[key] = [...fieldConfig.default[templateType]];
                    }
                } else {
                    updatedWidgetData[key] = fieldConfig.default;
                }
            }
        });
    
        // Only update state if something changed
        if (JSON.stringify(updatedWidgetData) !== JSON.stringify(formDetails.widget_data)) {
            setFormDetails((prev) => ({
                ...prev,
                widget_data: updatedWidgetData,
            }));
        }
    }, [additionalSettings]);

    // Call ensureMatchOrderExists on initial render
    useEffect(() => {
        ensureMatchOrderExists();
    }, []);

    // When template changes, ensure match_order has the correct values
    useEffect(() => {
        if (previousTemplate !== formDetails.template) {
            setPreviousTemplate(formDetails.template);
            
            const matchOrderField = additionalSettings.items.match_order;
            const templateType = formDetails.template;
            
            // Safely access the default match_order for this template
            if (matchOrderField?.default && 
                typeof matchOrderField.default === 'object' && 
                Array.isArray(matchOrderField.default[templateType])) {
                
                setFormDetails((prev) => {
                    return {
                        ...prev,
                        widget_data: {
                            ...prev.widget_data,
                            match_order: [...matchOrderField.default[templateType]]
                        }
                    };
                });
            }
        }
    }, [formDetails.template, previousTemplate]);

    // Ensure match_order items are correctly maintained
    useEffect(() => {
        if (!formDetails.widget_data) return;
        
        const matchOrderField = additionalSettings.items.match_order;
        const templateType = formDetails.template;
        
        // Ensure we have the current match_order and it's an array
        const currentOrder = Array.isArray(formDetails.widget_data.match_order) 
            ? formDetails.widget_data.match_order 
            : [];
            
        // Ensure default exists and is an array
        const defaultOrder = matchOrderField?.default && 
                            Array.isArray(matchOrderField.default[templateType]) 
                            ? matchOrderField.default[templateType] 
                            : [];
        
        if (defaultOrder.length > 0) {
            // Check if the arrays contain the same items (regardless of order)
            const currentSet = new Set(currentOrder);
            const defaultSet = new Set(defaultOrder);
            
            let needsUpdate = false;
            
            // Check if current has all required items and no extra items
            if (currentSet.size !== defaultSet.size) {
                needsUpdate = true;
            } else {
                for (const item of defaultSet) {
                    if (!currentSet.has(item)) {
                        needsUpdate = true;
                        break;
                    }
                }
            }
            
            // If we need to update, ensure we keep the current order where possible
            if (needsUpdate) {
                // Keep elements that are in the default set, in their current order
                const validCurrentItems = currentOrder.filter(item => defaultSet.has(item));
                
                // Add any missing items from default
                const missingItems = defaultOrder.filter(item => !currentSet.has(item));
                
                const correctedOrder = [...validCurrentItems, ...missingItems];
                
                setFormDetails(prev => ({
                    ...prev,
                    widget_data: {
                        ...prev.widget_data,
                        match_order: correctedOrder
                    }
                }));
            }
        }
    }, [formDetails.widget_data?.match_order, formDetails.template]);

    return (
        <Form>
            <Row>
                <Col>
                    {Object.entries(additionalSettings.items || {}).map(([key, fieldConfig]) => {
                        return shouldDisplayField(fieldConfig) ? (
                            <FormGroup key={key} className="px-4 mt-3">
                                <Row className="align-items-center">
                                    <Col sm="3">
                                        <div className="designLabel">
                                            <small className="text-default text-start d-block">
                                                {fieldConfig.label}
                                                {fieldConfig.required ? (
                                                    <span className="text-danger"> * </span>
                                                ) : (
                                                    ""
                                                )}
                                            </small>
                                        </div>
                                    </Col>
                                    <Col sm="6">
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                            {renderField(key, fieldConfig)}
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>
                        ) : null;
                    })}
                </Col>
            </Row>
        </Form>
    );
};

export default AdditionalSettings;